import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Check, AlertCircle, ChevronDown, ChevronUp } from 'lucide-react';
import api from '../../lib/axios';
import { useAuth } from '../../context/AuthContext';

const TIMEZONES = [
  { value: 'America/New_York',    label: 'Eastern Time — EST/EDT (New York, Miami, Atlanta)' },
  { value: 'America/Chicago',     label: 'Central Time — CST/CDT (Chicago, Houston, Dallas)' },
  { value: 'America/Denver',      label: 'Mountain Time — MST/MDT (Denver, Salt Lake City)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time — PST/PDT (Los Angeles, Seattle, Vegas)' },
  { value: 'America/Phoenix',     label: 'Arizona — MST (Phoenix, no DST)' },
  { value: 'America/Anchorage',   label: 'Alaska — AKST/AKDT' },
  { value: 'Pacific/Honolulu',    label: 'Hawaii — HST' },
  { value: 'America/Puerto_Rico', label: 'Atlantic Time — AST (Puerto Rico)' },
  { value: 'Europe/London',       label: 'London — GMT/BST' },
  { value: 'Europe/Paris',        label: 'Central Europe — CET/CEST (Paris, Berlin, Rome)' },
  { value: 'Asia/Dubai',          label: 'Dubai — GST (UAE)' },
  { value: 'Asia/Kolkata',        label: 'India — IST' },
  { value: 'Asia/Tokyo',          label: 'Japan — JST' },
  { value: 'Asia/Singapore',      label: 'Singapore — SGT' },
  { value: 'Australia/Sydney',    label: 'Sydney — AEST/AEDT' },
  { value: 'Pacific/Auckland',    label: 'New Zealand — NZST/NZDT' },
];

const businessSchema = z.object({
  name:                    z.string().min(2),
  email:                   z.string().email(),
  phone:                   z.string().optional(),
  timezone:                z.string(),
  currency:                z.string().length(3),
  primaryColor:            z.string().regex(/^#[0-9a-fA-F]{6}$/).optional().or(z.literal('')),
  logoUrl:                 z.string().url().optional().or(z.literal('')),
  bookingLeadTimeMinutes:  z.coerce.number().int().min(0),
  cancellationWindowHours: z.coerce.number().int().min(0),
});

const profileSchema = z.object({
  name:  z.string().min(2),
  email: z.string().email(),
});

const passwordSchema = z.object({
  currentPassword: z.string().min(1, 'Required'),
  newPassword:     z.string().min(8).regex(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/, 'Must contain uppercase, lowercase and a number'),
  confirmPassword: z.string(),
}).refine(d => d.newPassword === d.confirmPassword, { message: "Passwords don't match", path: ['confirmPassword'] });

function SaveBanner({ status }) {
  if (!status) return null;
  return (
    <div className={`flex items-center gap-2 text-xs px-3 py-2 rounded-lg ${
      status === 'ok'    ? 'bg-emerald-50 text-emerald-700' :
      status === 'error' ? 'bg-red-50 text-red-600' : ''}`}
    >
      {status === 'ok'    && <><Check size={13} /> Saved</>}
      {status === 'error' && <><AlertCircle size={13} /> Save failed — please try again.</>}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="card p-5 space-y-4">
      <h2 className="text-sm font-semibold text-ink border-b border-surface-border pb-3">{title}</h2>
      {children}
    </div>
  );
}

function Field({ label, error, children }) {
  return (
    <div>
      <label className="label">{label}</label>
      {children}
      {error && <p className="field-error">{error.message}</p>}
    </div>
  );
}

// ── Business settings ─────────────────────────────────────────────────────────
function BusinessSettings({ settings }) {
  const [status, setStatus] = useState(null);
  const { setTenant } = useAuth();
  const { register, handleSubmit, watch, reset, formState: { errors, isDirty } } = useForm({
    resolver: zodResolver(businessSchema),
    defaultValues: {
      name:                    settings.tenant.name,
      email:                   settings.tenant.email,
      phone:                   settings.tenant.phone || '',
      timezone:                settings.tenant.timezone,
      currency:                settings.tenant.currency,
      primaryColor:            settings.tenant.primaryColor || '#2E5BBA',
      logoUrl:                 settings.tenant.logoUrl || '',
      bookingLeadTimeMinutes:  settings.tenant.bookingLeadTimeMinutes,
      cancellationWindowHours: settings.tenant.cancellationWindowHours,
    },
  });

  const primaryColor = watch('primaryColor');

  const onSubmit = async (data) => {
    try {
      const { data: res } = await api.patch('/settings/business', data);
      // Update AuthContext tenant so sidebar/header reflect changes
      setTenant(prev => ({ ...prev, name: res.data.name, primaryColor: res.data.primaryColor }));
      setStatus('ok');
      reset(data);
      setTimeout(() => setStatus(null), 3000);
    } catch {
      setStatus('error');
    }
  };

  return (
    <Section title="Business profile">
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Business name *" error={errors.name}>
            <input {...register('name')} className="input w-full" />
          </Field>
          <Field label="Contact email *" error={errors.email}>
            <input type="email" {...register('email')} className="input w-full" />
          </Field>
          <Field label="Phone" error={errors.phone}>
            <input {...register('phone')} className="input w-full" placeholder="+1 555 000 0000" />
          </Field>
          <Field label="Timezone" error={errors.timezone}>
            <select {...register('timezone')} className="input w-full">
              {TIMEZONES.map(tz => <option key={tz.value} value={tz.value}>{tz.label}</option>)}
            </select>
          </Field>
          <Field label="Currency" error={errors.currency}>
            <select {...register('currency')} className="input w-full">
              {['USD','EUR','GBP','CAD','AUD','INR','AED'].map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </Field>
        </div>

        <hr className="border-surface-border" />
        <h3 className="text-xs font-semibold text-ink-muted uppercase tracking-wide">Branding</h3>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Brand color" error={errors.primaryColor}>
            <div className="flex items-center gap-3">
              <input type="color" {...register('primaryColor')} className="w-10 h-9 rounded border border-surface-border cursor-pointer p-0.5" />
              <input {...register('primaryColor')} className="input flex-1" placeholder="#2E5BBA" maxLength={7} />
              <div className="w-8 h-8 rounded-lg border border-surface-border shrink-0" style={{ background: primaryColor }} />
            </div>
          </Field>
          <Field label="Logo URL" error={errors.logoUrl}>
            <input {...register('logoUrl')} className="input w-full" placeholder="https://example.com/logo.png" />
          </Field>
        </div>

        <hr className="border-surface-border" />
        <h3 className="text-xs font-semibold text-ink-muted uppercase tracking-wide">Booking rules</h3>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Booking lead time (minutes)" error={errors.bookingLeadTimeMinutes}>
            <input type="number" {...register('bookingLeadTimeMinutes')} className="input w-full" min="0" max="10080" />
            <p className="text-xs text-ink-muted mt-1">Minimum notice before a client can book a slot.</p>
          </Field>
          <Field label="Cancellation window (hours)" error={errors.cancellationWindowHours}>
            <input type="number" {...register('cancellationWindowHours')} className="input w-full" min="0" max="168" />
            <p className="text-xs text-ink-muted mt-1">Clients cannot cancel within this many hours of their appointment.</p>
          </Field>
        </div>

        <div className="flex items-center gap-3 pt-1">
          <button type="submit" disabled={!isDirty} className="btn btn-primary">Save changes</button>
          <SaveBanner status={status} />
        </div>
      </form>
    </Section>
  );
}

// ── Profile settings ──────────────────────────────────────────────────────────
function ProfileSettings({ settings }) {
  const [status, setStatus] = useState(null);
  const { register, handleSubmit, reset, formState: { errors, isDirty } } = useForm({
    resolver: zodResolver(profileSchema),
    defaultValues: { name: settings.user.name, email: settings.user.email },
  });

  const onSubmit = async (data) => {
    try {
      await api.patch('/settings/profile', data);
      setStatus('ok');
      reset(data);
      setTimeout(() => setStatus(null), 3000);
    } catch {
      setStatus('error');
    }
  };

  return (
    <Section title="My profile">
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Full name *" error={errors.name}>
            <input {...register('name')} className="input w-full" />
          </Field>
          <Field label="Email *" error={errors.email}>
            <input type="email" {...register('email')} className="input w-full" />
          </Field>
        </div>
        <div className="flex items-center gap-3">
          <button type="submit" disabled={!isDirty} className="btn btn-primary">Save profile</button>
          <SaveBanner status={status} />
        </div>
      </form>
    </Section>
  );
}

// ── Password settings ─────────────────────────────────────────────────────────
function PasswordSettings() {
  const [status, setStatus] = useState(null);
  const [apiError, setApiError] = useState(null);
  const { register, handleSubmit, reset, formState: { errors } } = useForm({
    resolver: zodResolver(passwordSchema),
  });

  const onSubmit = async (data) => {
    setApiError(null);
    try {
      await api.patch('/settings/password', {
        currentPassword: data.currentPassword,
        newPassword:     data.newPassword,
      });
      setStatus('ok');
      reset();
      setTimeout(() => setStatus(null), 3000);
    } catch (err) {
      setApiError(err.response?.data?.error?.message || 'Failed to update password.');
      setStatus('error');
    }
  };

  return (
    <Section title="Change password">
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 max-w-sm">
        <Field label="Current password" error={errors.currentPassword}>
          <input type="password" {...register('currentPassword')} className="input w-full" autoComplete="current-password" />
        </Field>
        <Field label="New password" error={errors.newPassword}>
          <input type="password" {...register('newPassword')} className="input w-full" autoComplete="new-password" />
        </Field>
        <Field label="Confirm new password" error={errors.confirmPassword}>
          <input type="password" {...register('confirmPassword')} className="input w-full" autoComplete="new-password" />
        </Field>
        {apiError && <p className="field-error">{apiError}</p>}
        <div className="flex items-center gap-3">
          <button type="submit" className="btn btn-primary">Update password</button>
          {status === 'ok' && <SaveBanner status="ok" />}
        </div>
      </form>
    </Section>
  );
}

// ── Working hours settings ────────────────────────────────────────────────────
const DAY_LABELS = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];

function buildDefaultHours(raw = []) {
  const map = {};
  raw.forEach(h => { map[h.day_of_week] = h; });
  return Array.from({ length: 7 }, (_, i) => ({
    day_of_week: i,
    enabled:     !!map[i],
    start_time:  map[i]?.start_time || '09:00',
    end_time:    map[i]?.end_time   || '17:00',
  }));
}

function StaffHoursEditor({ member }) {
  const [open,   setOpen]   = useState(false);
  const [status, setStatus] = useState(null);
  const [days,   setDays]   = useState(null);

  useEffect(() => {
    if (!open || days) return;
    api.get(`/staff/${member.id}/working-hours`)
      .then(r => setDays(buildDefaultHours(r.data.data)))
      .catch(() => setDays(buildDefaultHours([])));
  }, [open, member.id, days]);

  const toggleDay = (i) => setDays(prev => prev.map((d, idx) => idx === i ? { ...d, enabled: !d.enabled } : d));
  const setTime   = (i, field, val) => setDays(prev => prev.map((d, idx) => idx === i ? { ...d, [field]: val } : d));

  const save = async () => {
    const payload = days.filter(d => d.enabled).map(({ day_of_week, start_time, end_time }) => ({ day_of_week, start_time, end_time }));
    try {
      await api.put(`/staff/${member.id}/working-hours`, payload);
      setStatus('ok');
      setTimeout(() => setStatus(null), 2500);
    } catch {
      setStatus('error');
    }
  };

  const initials = member.name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
  const color    = member.color || '#2E5BBA';

  return (
    <div className="border border-surface-border rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center gap-3 px-4 py-3 bg-surface-subtle hover:bg-surface-border/30 transition-colors"
      >
        <div className="w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-semibold shrink-0" style={{ background: color }}>
          {initials}
        </div>
        <span className="text-sm font-medium text-ink flex-1 text-left">{member.name}</span>
        {open ? <ChevronUp size={14} className="text-ink-muted" /> : <ChevronDown size={14} className="text-ink-muted" />}
      </button>

      {open && (
        <div className="px-4 pb-4 pt-2 space-y-2 border-t border-surface-border">
          {!days ? (
            <p className="text-xs text-ink-muted py-2">Loading…</p>
          ) : (
            <>
              <div className="space-y-1.5">
                {days.map((day, i) => (
                  <div key={i} className={`flex items-center gap-3 rounded-lg px-3 py-2 transition-colors ${day.enabled ? 'bg-blue-50' : 'bg-surface-subtle'}`}>
                    <input
                      type="checkbox"
                      checked={day.enabled}
                      onChange={() => toggleDay(i)}
                      className="w-4 h-4 accent-primary cursor-pointer"
                      id={`${member.id}-day-${i}`}
                    />
                    <label htmlFor={`${member.id}-day-${i}`} className="w-24 text-sm font-medium text-ink cursor-pointer select-none">
                      {DAY_LABELS[i]}
                    </label>
                    {day.enabled ? (
                      <div className="flex items-center gap-2 flex-1">
                        <input
                          type="time"
                          value={day.start_time}
                          onChange={e => setTime(i, 'start_time', e.target.value)}
                          className="input text-sm py-1 px-2 flex-1"
                        />
                        <span className="text-xs text-ink-muted">to</span>
                        <input
                          type="time"
                          value={day.end_time}
                          onChange={e => setTime(i, 'end_time', e.target.value)}
                          className="input text-sm py-1 px-2 flex-1"
                        />
                      </div>
                    ) : (
                      <span className="text-xs text-ink-muted italic">Closed</span>
                    )}
                  </div>
                ))}
              </div>
              <div className="flex items-center gap-3 pt-1">
                <button type="button" onClick={save} className="btn btn-primary text-sm py-1.5 px-4">Save hours</button>
                <SaveBanner status={status} />
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function WorkingHoursSettings() {
  const { data: staffList, isLoading } = useQuery({
    queryKey: ['staff-all'],
    queryFn: () => api.get('/staff').then(r => r.data.data),
    staleTime: 60_000,
  });

  return (
    <Section title="Working hours">
      <p className="text-xs text-ink-muted -mt-1">Set each staff member's availability by day. Clients can only book during these hours.</p>
      {isLoading ? (
        <p className="text-sm text-ink-muted">Loading staff…</p>
      ) : !staffList?.length ? (
        <p className="text-sm text-ink-muted">No staff members found. Add staff first.</p>
      ) : (
        <div className="space-y-2">
          {staffList.map(m => <StaffHoursEditor key={m.id} member={m} />)}
        </div>
      )}
    </Section>
  );
}

// ── Messaging (Twilio) settings ───────────────────────────────────────────────
function TwilioSettings() {
  const [status, setStatus] = useState(null);
  const { register, handleSubmit, formState: { errors, isDirty } } = useForm({
    defaultValues: { twilioAccountSid: '', twilioAuthToken: '', twilioFromNumber: '' },
  });

  const onSubmit = async (data) => {
    try {
      await api.patch('/settings/business', {
        twilioAccountSid: data.twilioAccountSid || null,
        twilioAuthToken:  data.twilioAuthToken  || null,
        twilioFromNumber: data.twilioFromNumber  || null,
      });
      setStatus('ok');
      setTimeout(() => setStatus(null), 3000);
    } catch {
      setStatus('error');
    }
  };

  return (
    <Section title="Messaging (Twilio)">
      <div className="flex items-start gap-3 p-3 rounded-lg bg-blue-50 border border-blue-200 mb-2">
        <AlertCircle size={15} className="text-blue-500 mt-0.5 shrink-0" />
        <p className="text-xs text-blue-700">Required to send SMS promotional messages and birthday greetings to customers.</p>
      </div>
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Account SID" error={errors.twilioAccountSid}>
            <input {...register('twilioAccountSid')} className="input w-full" placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx" />
          </Field>
          <Field label="Auth Token" error={errors.twilioAuthToken}>
            <input type="password" {...register('twilioAuthToken')} className="input w-full" placeholder="••••••••••••••••••••••••••••••••" />
          </Field>
          <Field label="From Number" error={errors.twilioFromNumber}>
            <input {...register('twilioFromNumber')} className="input w-full" placeholder="+1XXXXXXXXXX" />
          </Field>
        </div>
        <div className="flex items-center gap-3 pt-1">
          <button type="submit" disabled={!isDirty} className="btn btn-primary">Save changes</button>
          <SaveBanner status={status} />
        </div>
      </form>
    </Section>
  );
}

// ── Payments settings ─────────────────────────────────────────────────────────
function parseIdArray(val) {
  if (Array.isArray(val)) return val;
  if (typeof val === 'string') { try { const p = JSON.parse(val); return Array.isArray(p) ? p : []; } catch { return []; } }
  return [];
}

function PaymentsSettings({ settings }) {
  const [status, setStatus]             = useState(null);
  const [cashappIds, setCashappIds]     = useState(parseIdArray(settings?.tenant?.cashappIds));
  const [zelleIds,   setZelleIds]       = useState(parseIdArray(settings?.tenant?.zelleIds));
  const [depositMsg, setDepositMsg]     = useState(settings?.tenant?.depositMessage || '');
  const [newCashapp, setNewCashapp]     = useState('');
  const [newZelle,   setNewZelle]       = useState('');

  const addCashapp = () => {
    const v = newCashapp.trim();
    if (v && !cashappIds.includes(v)) { setCashappIds(p => [...p, v]); }
    setNewCashapp('');
  };
  const addZelle = () => {
    const v = newZelle.trim();
    if (v && !zelleIds.includes(v)) { setZelleIds(p => [...p, v]); }
    setNewZelle('');
  };

  const save = async () => {
    try {
      await api.patch('/settings/business', {
        cashappIds:     cashappIds,
        zelleIds:       zelleIds,
        depositMessage: depositMsg || null,
      });
      setStatus('ok');
      setTimeout(() => setStatus(null), 3000);
    } catch {
      setStatus('error');
    }
  };

  return (
    <Section title="Payments">
      <div className="flex items-start gap-3 p-3 rounded-lg bg-gray-50 border border-gray-200 mb-2">
        <AlertCircle size={15} className="text-gray-500 mt-0.5 shrink-0" />
        <p className="text-xs text-gray-600">Customers will see these payment options as a step after confirming their booking details.</p>
      </div>

      {/* Deposit message */}
      <div>
        <label className="label">Deposit message shown to customers</label>
        <textarea
          value={depositMsg}
          onChange={e => setDepositMsg(e.target.value)}
          rows={3}
          className="input resize-none w-full"
          placeholder="Please send a $25 deposit to secure your appointment. Once received, we will confirm your booking by call."
        />
        <p className="text-xs text-ink-muted mt-1">This message appears above the payment IDs on the booking confirmation step.</p>
      </div>

      {/* CashApp IDs */}
      <div>
        <label className="label">CashApp IDs</label>
        <div className="space-y-2">
          {cashappIds.map((id, i) => (
            <div key={i} className="flex items-center gap-2">
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-green-50 border border-green-200 text-green-800 text-sm rounded-lg flex-1">
                <span className="font-bold text-green-600">$</span>{id}
              </span>
              <button type="button" onClick={() => setCashappIds(p => p.filter((_, j) => j !== i))}
                className="text-xs text-red-500 hover:text-red-700 px-2 py-1 rounded hover:bg-red-50">Remove</button>
            </div>
          ))}
          <div className="flex gap-2">
            <input
              value={newCashapp}
              onChange={e => setNewCashapp(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addCashapp())}
              className="input flex-1"
              placeholder="$yourcashtag"
            />
            <button type="button" onClick={addCashapp} className="btn btn-secondary text-sm px-3">Add</button>
          </div>
        </div>
      </div>

      {/* Zelle IDs */}
      <div>
        <label className="label">Zelle IDs</label>
        <div className="space-y-2">
          {zelleIds.map((id, i) => (
            <div key={i} className="flex items-center gap-2">
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-purple-50 border border-purple-200 text-purple-800 text-sm rounded-lg flex-1">
                <span className="font-bold text-purple-600">Z</span>{id}
              </span>
              <button type="button" onClick={() => setZelleIds(p => p.filter((_, j) => j !== i))}
                className="text-xs text-red-500 hover:text-red-700 px-2 py-1 rounded hover:bg-red-50">Remove</button>
            </div>
          ))}
          <div className="flex gap-2">
            <input
              value={newZelle}
              onChange={e => setNewZelle(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addZelle())}
              className="input flex-1"
              placeholder="phone number or email"
            />
            <button type="button" onClick={addZelle} className="btn btn-secondary text-sm px-3">Add</button>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-3 pt-1">
        <button type="button" onClick={save} className="btn btn-primary">Save changes</button>
        <SaveBanner status={status} />
      </div>
    </Section>
  );
}

// ── Birthday Campaign settings ────────────────────────────────────────────────
function BirthdayCampaignSettings() {
  const [status, setStatus] = useState(null);
  const { register, handleSubmit, reset, formState: { isDirty } } = useForm({
    defaultValues: { active: false, smsMessage: '', emailSubject: '', emailBody: '' },
  });

  const { data: campaign, isLoading } = useQuery({
    queryKey: ['birthday-campaign'],
    queryFn: () => api.get('/settings/birthday-campaign').then(r => r.data.data),
    staleTime: 60_000,
  });

  useEffect(() => {
    if (campaign) {
      reset({
        active:       campaign.active       || false,
        smsMessage:   campaign.smsMessage   || '',
        emailSubject: campaign.emailSubject || '',
        emailBody:    campaign.emailBody    || '',
      });
    }
  }, [campaign, reset]);

  const onSubmit = async (data) => {
    try {
      await api.put('/settings/birthday-campaign', {
        active:       data.active,
        smsMessage:   data.smsMessage,
        emailSubject: data.emailSubject,
        emailBody:    data.emailBody,
      });
      setStatus('ok');
      setTimeout(() => setStatus(null), 3000);
    } catch {
      setStatus('error');
    }
  };

  if (isLoading) return <Section title="Birthday Campaign"><p className="text-sm text-ink-muted">Loading…</p></Section>;

  return (
    <Section title="Birthday Campaign">
      <div className="flex items-start gap-3 p-3 rounded-lg bg-blue-50 border border-blue-200 mb-2">
        <AlertCircle size={15} className="text-blue-500 mt-0.5 shrink-0" />
        <p className="text-xs text-blue-700">Use <code>{'{{name}}'}</code> to personalize messages. SMS requires Twilio to be configured.</p>
      </div>
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div className="flex items-center gap-2">
          <input {...register('active')} type="checkbox" id="birthday-active" className="w-4 h-4 accent-primary" />
          <label htmlFor="birthday-active" className="text-sm text-ink font-medium">Enable birthday campaign</label>
        </div>
        <Field label="SMS Message">
          <textarea
            {...register('smsMessage')}
            rows={3}
            className="input resize-none w-full"
            placeholder="Happy Birthday {{name}}! Here's a special offer just for you..."
          />
        </Field>
        <Field label="Email Subject">
          <input {...register('emailSubject')} className="input w-full" placeholder="Happy Birthday, {{name}}!" />
        </Field>
        <Field label="Email Body">
          <textarea
            {...register('emailBody')}
            rows={4}
            className="input resize-none w-full"
            placeholder="Hi {{name}}, wishing you a wonderful birthday! ..."
          />
        </Field>
        <div className="flex items-center gap-3 pt-1">
          <button type="submit" disabled={!isDirty} className="btn btn-primary">Save campaign</button>
          <SaveBanner status={status} />
        </div>
      </form>
    </Section>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
const SETTINGS_TABS = [
  { id: 'business',  label: 'Business' },
  { id: 'messaging', label: 'Messaging (Twilio)' },
  { id: 'payments',  label: 'Payments' },
  { id: 'birthday',  label: 'Birthday Campaign' },
  { id: 'account',   label: 'Account' },
];

export default function Settings() {
  const [activeTab, setActiveTab] = useState('business');

  const { data: settings, isLoading, isError, error } = useQuery({
    queryKey: ['settings'],
    queryFn: () => api.get('/settings').then(r => r.data.data),
    retry: 1,
  });

  if (isLoading) return <div className="p-6 text-ink-muted text-sm">Loading…</div>;

  if (isError) return (
    <div className="p-6 max-w-2xl">
      <div className="card p-5 border-red-200 bg-red-50">
        <div className="flex items-start gap-3">
          <AlertCircle size={16} className="text-red-500 mt-0.5 shrink-0" />
          <div>
            <p className="text-sm font-semibold text-red-700">Failed to load settings</p>
            <p className="text-xs text-red-600 mt-1">
              {error?.response?.data?.error?.message || error?.message || 'Could not reach the API. Make sure the backend is running.'}
            </p>
            <p className="text-xs text-red-500 mt-2">
              Check: <code>GET /api/settings</code> in the browser console (F12 → Network tab).
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  if (!settings) return null;

  return (
    <div className="p-6 max-w-2xl space-y-6">
      <div>
        <h1 className="text-lg font-semibold text-ink">Settings</h1>
        <p className="text-sm text-ink-secondary mt-0.5">Manage your business profile and account</p>
      </div>

      {/* Tab bar */}
      <div className="flex gap-1 border-b border-surface-border overflow-x-auto pb-0">
        {SETTINGS_TABS.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-3 py-2 text-sm font-medium whitespace-nowrap border-b-2 transition-all -mb-px
              ${activeTab === tab.id
                ? 'border-primary text-primary'
                : 'border-transparent text-ink-muted hover:text-ink hover:border-surface-border'}`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'business' && (
        <>
          <BusinessSettings settings={settings} />
          <WorkingHoursSettings />
        </>
      )}
      {activeTab === 'messaging' && <TwilioSettings />}
      {activeTab === 'payments'  && <PaymentsSettings settings={settings} />}
      {activeTab === 'birthday'  && <BirthdayCampaignSettings />}
      {activeTab === 'account'   && (
        <>
          <ProfileSettings settings={settings} />
          <PasswordSettings />
        </>
      )}
    </div>
  );
}
